import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import DailyIframe from "@daily-co/daily-js";
import { obs, ObsEvent, LatencyTracker } from "../../services/observability";

function getOrCreateCallObject() {
    if (!window.__tavusDailyCallObject) {
        window.__tavusDailyCallObject = DailyIframe.createCallObject();
    }
    return window.__tavusDailyCallObject;
}

const TavusStream = forwardRef(({
    conversationUrl,
    meetingToken,
    sessionId,
    mediaStream,
    onRoomConnected,
    onRemoteParticipantJoined,
    onRoomError,
}, ref) => {
    const videoRef = useRef(null);
    const audioRef = useRef(null);
    const callRef = useRef(null);
    const connectTrackerRef = useRef(null);

    const onRoomConnectedRef = useRef(onRoomConnected);
    useEffect(() => {
        onRoomConnectedRef.current = onRoomConnected;
    }, [onRoomConnected]);

    const onRoomErrorRef = useRef(onRoomError);
    useEffect(() => {
        onRoomErrorRef.current = onRoomError;
    }, [onRoomError]);

    const onRemoteJoinedRef = useRef(onRemoteParticipantJoined);
    useEffect(() => {
        onRemoteJoinedRef.current = onRemoteParticipantJoined;
    }, [onRemoteParticipantJoined]);
    const remoteNotifiedRef = useRef(false);

    useImperativeHandle(ref, () => ({
        sendSpeakCommand: async () => {},
        sendLiveKitCommand: async () => {},
    }));

    useEffect(() => {
        if (!conversationUrl) {
            return undefined;
        }

        let isUnmounted = false;
        remoteNotifiedRef.current = false;
        connectTrackerRef.current = new LatencyTracker().start();
        const call = getOrCreateCallObject();
        callRef.current = call;

        const noteRemoteParticipant = (participant) => {
            if (!participant || participant.local || remoteNotifiedRef.current) {
                return;
            }
            remoteNotifiedRef.current = true;
            if (typeof onRemoteJoinedRef.current === "function") {
                onRemoteJoinedRef.current();
            }
        };

        const attachRemote = (participant) => {
            if (!participant || participant.local) {
                return;
            }
            noteRemoteParticipant(participant);
            const videoTrack = participant.tracks?.video?.persistentTrack;
            const audioTrack = participant.tracks?.audio?.persistentTrack;
            if (videoRef.current && videoTrack) {
                videoRef.current.srcObject = new MediaStream([videoTrack]);
            }
            if (audioRef.current && audioTrack) {
                audioRef.current.srcObject = new MediaStream([audioTrack]);
                audioRef.current.play().catch((err) => {
                    console.error("[TavusStream] Failed to play remote audio:", err);
                });
            }
        };

        const handleJoined = () => {
            const connectionMs = connectTrackerRef.current?.elapsed() || null;
            obs.emit(ObsEvent.LIVEKIT_ROOM_CONNECTED, {
                session_id: sessionId,
                connection_ms: connectionMs,
                status: "success",
                mode: "VIDEO",
            });
            const existing = call.participants?.() || {};
            Object.values(existing).forEach(attachRemote);
            if (typeof onRoomConnectedRef.current === "function") {
                onRoomConnectedRef.current();
            }
        };

        const handleError = (event) => {
            const message = event?.errorMsg || event?.error?.message || "Daily connection failed";
            obs.emit(ObsEvent.LIVEKIT_ROOM_DISCONNECTED, {
                session_id: sessionId,
                error_message: message,
                status: "error",
                mode: "VIDEO",
            });
            if (typeof onRoomErrorRef.current === "function") {
                onRoomErrorRef.current(message);
            }
        };

        const handleParticipant = (event) => attachRemote(event?.participant);
        const handleTrackStarted = (event) => {
            if (event?.participant?.local) {
                return;
            }
            attachRemote(event?.participant);
        };

        call.on("joined-meeting", handleJoined);
        call.on("error", handleError);
        call.on("participant-joined", handleParticipant);
        call.on("participant-updated", handleParticipant);
        call.on("track-started", handleTrackStarted);

        const joinOptions = { url: conversationUrl };
        if (typeof meetingToken === "string" && meetingToken.length > 0) {
            joinOptions.token = meetingToken;
        }
        if (mediaStream) {
            const audioTrack = mediaStream.getAudioTracks()[0];
            const videoTrack = mediaStream.getVideoTracks()[0];
            if (audioTrack) {
                joinOptions.audioSource = audioTrack;
            }
            if (videoTrack) {
                joinOptions.videoSource = videoTrack;
            }
        }

        call.join(joinOptions).catch((error) => {
            if (!isUnmounted) {
                handleError({ errorMsg: error?.message || "Failed to join Tavus room" });
            }
        });

        return () => {
            isUnmounted = true;
            call.off("joined-meeting", handleJoined);
            call.off("error", handleError);
            call.off("participant-joined", handleParticipant);
            call.off("participant-updated", handleParticipant);
            call.off("track-started", handleTrackStarted);
            call.leave().catch(() => undefined);
            call.destroy().catch(() => undefined);
            if (window.__tavusDailyCallObject === call) {
                window.__tavusDailyCallObject = null;
            }
            callRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationUrl, meetingToken]);

    return (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <audio ref={audioRef} autoPlay playsInline />
        </div>
    );
});

TavusStream.displayName = "TavusStream";

export default TavusStream;
